"use client"

import * as React from "react"

import {
  SearchResultKind,
  type MemberSearchResult,
  type SearchResult,
} from "@ibada/shared-types/search"
import { Button } from "@ibada/ui"

import { MemberStatus } from "@/features/members/model/members-types"
import {
  MemberAvatar,
  MemberContactButtons,
  PreviewFact,
  StatusPill,
} from "@/features/members/components/members-views"
import { SearchKindTile } from "@/features/search/components/search-kind-meta"
import { useSearchLabels } from "@/features/search/hooks/use-search-labels"
import { formatSearchAmount } from "@/features/search/lib/search-money"
import type { SearchPaletteEntry } from "@/features/search/model/search-palette-types"
import { useLocale, useTranslations } from "@/i18n"
import { useFormatDate } from "@/shared/lib/dates/format-date"

type Fact = { label: string; value: string }

const MEMBER_STATUSES = new Set<string>(Object.values(MemberStatus))

function toMemberStatus(value: string): MemberStatus | null {
  const normalized = value.toLowerCase()
  return MEMBER_STATUSES.has(normalized) ? (normalized as MemberStatus) : null
}

function compactFacts(facts: Array<Fact | null>): Fact[] {
  return facts.filter((fact): fact is Fact => fact !== null && fact.value.trim().length > 0)
}

function fact(label: string, value: string | number | null | undefined): Fact | null {
  if (value === null || value === undefined || value === "") return null
  return { label, value: String(value) }
}

/** The right-hand pane of the palette: the highlighted entry with its key facts and main actions. */
export function SearchPreview({ entry, onOpen }: { entry: SearchPaletteEntry; onOpen: (entry: SearchPaletteEntry) => void }) {
  if (entry.result?.kind === SearchResultKind.Member) {
    return <MemberSearchPreview result={entry.result} onOpen={() => onOpen(entry)} />
  }
  return <GenericSearchPreview entry={entry} onOpen={() => onOpen(entry)} />
}

function MemberSearchPreview({ result, onOpen }: { result: MemberSearchResult; onOpen: () => void }) {
  const t = useTranslations("search.preview")
  const tMembers = useTranslations("people.members.preview")
  const status = toMemberStatus(result.status)
  const phone = result.phone?.trim() || null
  const affiliations = [...result.groupNames, ...result.ministryNames].filter(Boolean).join(", ")
  const facts = compactFacts([
    fact(t("phone"), phone),
    fact(t("branch"), result.branchName),
    fact(t("groups"), affiliations),
    fact(t("occupation"), result.occupation),
  ])

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4'>
      <div className='flex flex-col items-center gap-2 text-center'>
        <MemberAvatar member={{ name: result.title, avatarUrl: result.avatarUrl }} className='size-16 text-xl' />
        <span className='line-clamp-2 text-[17px] font-extrabold'>{result.title}</span>
        {status ? <StatusPill status={status} /> : null}
      </div>
      <FactList facts={facts} />
      <div className='mt-auto flex flex-col gap-2'>
        <Button className='w-full' onClick={onOpen}>
          {tMembers("openProfile")}
        </Button>
        {phone ? (
          <div className='flex gap-2'>
            <MemberContactButtons phone={phone} buttonClassName='flex-1' />
          </div>
        ) : null}
      </div>
    </div>
  )
}

function GenericSearchPreview({ entry, onOpen }: { entry: SearchPaletteEntry; onOpen: () => void }) {
  const t = useTranslations("search.preview")
  const labels = useSearchLabels()
  const facts = useResultFacts(entry.result)
  const status = entry.result && "status" in entry.result ? labels.statusLabel(entry.result.status) : null
  const caption = entry.recent ? t("recent") : labels.kindLabel(entry.kind)

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4'>
      <div className='flex flex-col items-center gap-2 text-center'>
        <SearchKindTile kind={entry.kind} icon={entry.icon} recent={entry.recent} className='size-16 rounded-2xl [&_svg]:size-7' />
        <span className='line-clamp-2 text-[17px] font-extrabold'>{entry.title}</span>
        <span className='text-muted-foreground text-xs font-semibold'>{caption}</span>
        {status ? (
          <span className='bg-muted text-foreground/80 inline-flex h-5 items-center rounded-full px-2 text-[11px] font-bold'>{status}</span>
        ) : null}
      </div>
      {entry.subtitle && facts.length === 0 ? <p className='text-muted-foreground text-center text-sm'>{entry.subtitle}</p> : null}
      <FactList facts={facts} />
      <div className='mt-auto'>
        <Button className='w-full' onClick={onOpen}>
          {t("open")}
        </Button>
      </div>
    </div>
  )
}

function FactList({ facts }: { facts: Fact[] }) {
  if (facts.length === 0) return null
  return (
    <dl className='grid min-h-0 gap-2 overflow-y-auto'>
      {facts.map((item) => (
        <PreviewFact key={item.label} label={item.label} value={item.value} />
      ))}
    </dl>
  )
}

/** Key facts per kind. Amounts appear only when the server disclosed them (not null). */
function useResultFacts(result: SearchResult | null): Fact[] {
  const t = useTranslations("search.preview")
  const labels = useSearchLabels()
  const locale = useLocale()
  const formatDate = useFormatDate()
  if (!result) return []

  switch (result.kind) {
    case SearchResultKind.Transaction:
      return compactFacts([
        fact(t("amount"), formatSearchAmount(result.amount, result.currency, locale)),
        fact(t("type"), labels.transactionKindLabel(result.transactionKind)),
        fact(t("date"), result.date ? formatDate(result.date) : null),
        fact(t("account"), result.accountName),
        fact(t("reference"), result.reference),
      ])
    case SearchResultKind.Pledge: {
      const total = formatSearchAmount(result.totalAmount, result.currency, locale)
      const fulfilled = formatSearchAmount(result.fulfilledAmount, result.currency, locale)
      return compactFacts([
        fact(t("pledger"), result.pledgerName),
        fact(t("pledged"), total),
        fact(t("fulfilled"), fulfilled),
        fact(t("fund"), result.fundName),
      ])
    }
    case SearchResultKind.Activity:
      return compactFacts([
        fact(t("startDate"), result.startDate ? formatDate(result.startDate) : null),
        fact(t("endDate"), result.endDate && result.endDate !== result.startDate ? formatDate(result.endDate) : null),
        fact(t("details"), result.subtitle),
      ])
    case SearchResultKind.Group:
    case SearchResultKind.Ministry:
    case SearchResultKind.Department:
      return compactFacts([
        fact(t("leader"), result.leaderName),
        fact(t("memberCount"), result.memberCount),
        fact(t("description"), result.description),
      ])
    case SearchResultKind.Form:
      return compactFacts([fact(t("responseCount"), result.responseCount), fact(t("details"), result.subtitle)])
    case SearchResultKind.Asset:
      return compactFacts([fact(t("itemCode"), result.itemCode), fact(t("details"), result.subtitle)])
    case SearchResultKind.Member:
      return []
  }
}
